/**
 * MuiSalespersonsList - Optimized Salespersons List Page
 *
 * OPTIMIZATION: Phase 10.1 - Server-side pagination implementation
 *
 * Changes made:
 * - Replaced getSalesPersons() with usePaginatedData(getSalespersonsPaginated) hook
 * - Added debounced search (300ms) - server-side text search across name, email, phone
 * - Server-side pagination with limit/offset
 * - Server-side sorting by name ASC (default)
 * - DataGrid now uses paginationMode="server" and sortingMode="server"
 *
 * Benefits:
 * - Only loads visible page data (e.g., 25 records instead of full list)
 * - Search queries happen on server, reducing client memory usage
 * - Faster initial load and navigation between pages
 */
import React, { useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Container,
  TextField,
  InputAdornment,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import SearchIcon from "@mui/icons-material/Search"
import PersonIcon from "@mui/icons-material/Person"
import BadgeIcon from "@mui/icons-material/Badge"

// MUI Components
import MuiPageHeader from "../../components/mui/MuiPageHeader"
import MuiCard from "../../components/mui/MuiCard"

// API and hooks
import { getSalespersonsPaginated } from "helpers/api_helper"
import { usePaginatedData } from "../../hooks/usePaginatedData"

/**
 * MuiSalespersonsList - Salespersons list page with DataGrid
 *
 * UX Laws Applied:
 * - Miller's Law: Shows 3 key columns (ID, Name, Surname) for simple data
 * - Jakob's Law: Standard DataGrid pattern with sorting and search
 * - Law of Proximity: Search and table grouped together
 * - Fitts's Law: Large row click targets for navigation
 * - Aesthetic-Usability Effect: Clean avatars and consistent styling
 */
const MuiSalespersonsList = () => {
  const navigate = useNavigate()

  document.title = "Salespersons List | FlexFlow ERP"

  // ============================================================================
  // SERVER-SIDE PAGINATION via usePaginatedData hook
  // ============================================================================
  const {
    data: salespersons,
    total,
    loading,
    error,
    pagination,
    searchTerm,
    sortModel,
    handlePageChange,
    handlePageSizeChange,
    handleSearchChange,
    handleSortChange,
    refresh
  } = usePaginatedData(getSalespersonsPaginated, {
    initialPageSize: 25,
    initialSort: { field: "name", direction: "ASC" },
    debounceMs: 300,
    autoFetch: true
  })

  // Generate initials for avatar
  const getInitials = (name, surname) => {
    const first = name?.charAt(0)?.toUpperCase() || ""
    const last = surname?.charAt(0)?.toUpperCase() || ""
    return first + last || "SP"
  }

  // Generate consistent color based on name
  const stringToColor = (string) => {
    let hash = 0
    for (let i = 0; i < string?.length || 0; i++) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash)
    }
    let color = "#"
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff
      color += `00${value.toString(16)}`.slice(-2)
    }
    return color
  }

  // DataGrid columns
  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: 80,
        renderCell: (params) => (
          <Chip
            label={params.value}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        ),
      },
      {
        field: "name",
        headerName: "First Name",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: stringToColor(params.row.name + params.row.surname),
                fontSize: "0.875rem",
              }}
            >
              {getInitials(params.row.name, params.row.surname)}
            </Avatar>
            <Typography variant="body2" fontWeight={500}>
              {params.value || "-"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "surname",
        headerName: "Last Name",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => (
          <Typography variant="body2">{params.value || "-"}</Typography>
        ),
      },
      {
        field: "fullName",
        headerName: "Full Name",
        flex: 1.5,
        minWidth: 200,
        sortable: false, // Computed field, can't sort server-side
        valueGetter: (value, row) =>
          `${row.name || ""} ${row.surname || ""}`.trim() || "-",
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BadgeIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        ),
      },
    ],
    []
  )

  // Handle row click
  const handleRowClick = useCallback((params) => {
    navigate(`/salesperson-detail/${params.row.id}`)
  }, [navigate])

  // Handle DataGrid pagination model change
  const handlePaginationModelChange = useCallback((model) => {
    if (model.pageSize !== pagination.pageSize) {
      handlePageSizeChange(model.pageSize)
    }
    if (model.page !== pagination.page) {
      handlePageChange(model.page)
    }
  }, [pagination.page, pagination.pageSize, handlePageChange, handlePageSizeChange])

  // Handle DataGrid sort model change
  const handleSortModelChange = useCallback((model) => {
    if (model.length > 0) {
      const { field, sort } = model[0]
      handleSortChange(field, sort === "asc" ? "ASC" : "DESC")
    }
  }, [handleSortChange])

  // Convert sort model for DataGrid format
  const dataGridSortModel = useMemo(() => {
    if (sortModel.field) {
      return [{ field: sortModel.field, sort: sortModel.direction.toLowerCase() }]
    }
    return [{ field: "name", sort: "asc" }]
  }, [sortModel])

  // Breadcrumb configuration
  const breadcrumbs = [
    { label: "Directory", path: "/" },
    { label: "Salespersons" },
  ]

  return (
    <div className="page-content">
      <Container maxWidth={false}>
        {/* Page Header */}
        <MuiPageHeader
          title="Salespersons"
          subtitle={`${total} salespersons found`}
          breadcrumbs={breadcrumbs}
        />

        {/* Search Section */}
        <MuiCard sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <TextField
              size="small"
              placeholder="Search by ID, name, or surname..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 300, flex: 1, maxWidth: 400 }}
            />
            <Typography variant="body2" color="text.secondary">
              {total} salespersons
            </Typography>
          </Box>
        </MuiCard>

        {/* DataGrid */}
        <MuiCard noPadding>
          {loading && salespersons.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 400,
              }}
            >
              <CircularProgress />
            </Box>
          ) : salespersons.length > 0 || loading ? (
            <Box sx={{ width: "100%", height: 600 }}>
              <DataGrid
                rows={salespersons}
                columns={columns}
                getRowId={(row) => row.id}
                loading={loading}
                // Server-side pagination
                paginationMode="server"
                rowCount={total}
                paginationModel={{
                  page: pagination.page,
                  pageSize: pagination.pageSize,
                }}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={[10, 25, 50, 100, 150]}
                // Server-side sorting
                sortingMode="server"
                sortModel={dataGridSortModel}
                onSortModelChange={handleSortModelChange}
                // Other settings
                onRowClick={handleRowClick}
                disableRowSelectionOnClick
                sx={{
                  border: 0,
                  "& .MuiDataGrid-row": {
                    cursor: "pointer",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "action.hover",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "grey.50",
                    borderBottom: 2,
                    borderColor: "divider",
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: 1,
                    borderColor: "divider",
                  },
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-cell:focus-within": {
                    outline: "none",
                  },
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
              }}
            >
              <PersonIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No salespersons found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm
                  ? "Try adjusting your search criteria"
                  : "No salesperson records available"}
              </Typography>
            </Box>
          )}
        </MuiCard>
      </Container>
    </div>
  )
}

MuiSalespersonsList.propTypes = {
  customers: PropTypes.array,
  onGetInvoices: PropTypes.func,
}

export default MuiSalespersonsList
